import React, { useEffect, useState, forwardRef } from 'react';

import {
    Box,
    Typography,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableCell,
    TableRow,
    TableBody,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    TextField,
    Grid,
    FormHelperText,
    Divider,
    Checkbox,
    FormGroup,
    FormControlLabel,
    InputAdornment
} from '@mui/material';

import dayjs from 'dayjs';

import { useFormik } from 'formik';

import { useAppContextFeatures } from '../../../context/AppContext';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';

import { useDocumentValidation } from "../../../hooks/api/useDocumentValidation"
import { CommonIcons, icons } from '../../common/CommonIcons';

import { initialValues, validationSchema } from '../../../schema/documents/validation/verifyInSchema';

import { useSystemUtils } from '../../../hooks/useSystemUtils';
import { useTranslations } from '../../../hooks/useTranslations';

import DatePickerComponent from '../../common/DatePickerComponent';


const RenderRackSelect = ({
    formik, t, getRacks
}) => {
    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;
    return (
          <FormControl
                margin="dense"
                fullWidth
                size="small"
                error={touched.rackId && Boolean(errors.rackId)}
          >
              <InputLabel id="rack-label">{t('rack')}</InputLabel>
              <Select
                    labelId="rack-label"
                    id="rack_id"
                    value={values?.rackId}
                    name={`rackId`}
                    label={t('rack')}
                    margin="dense"
                    size="small"
                    onChange={(event) => {
                        setFieldValue(`rackId`, event.target.value)
                    }}
              >
                  {
                      getRacks().map((item, index) => <MenuItem key={index} value={item.id}>{item.rack_name}</MenuItem>)
                  }
              </Select>
              <FormHelperText>
                  {touched.rackId && errors.rackId}
              </FormHelperText>
          </FormControl>
    )
}

const RenderSlotSelect = ({
    formik,
    getSlots,
    t
}) => {

    // if(!values.rackId) return <></>
    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;
    const items = getSlots({ rackId: values?.rackId });

    return (
          <FormControl
                margin="dense"
                fullWidth
                size="small"
                error={touched.slotId && Boolean(errors.slotId)}
          >
              <InputLabel id="documentType">{t('slot')}</InputLabel>
              <Select
                    id="slotSelection"
                    value={values?.slotId}
                    name={`slotId`}
                    label={t('slot')}
                    margin="dense"
                    size="small"
                    onChange={(event) => {
                        setFieldValue(`slotId`, event.target.value)
                    }}
              >
                  {
                      items.map((item, index) => <MenuItem key={index} value={item.id}>{item.name}</MenuItem>)
                  }
              </Select>
              <FormHelperText>
                  {touched.slotId && errors.slotId}
              </FormHelperText>
          </FormControl>
    )

}

const RenderBatch = ({
    t,
    formik
}) => {

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;
    if (!values?.batch?.enableTrackBatch) return <></>

    return (
          <>
              <Grid item xs={12} sm={12} md={6}>
                  <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"batch.customBatchNo"}
                        label={t("batchNumber")}
                        type="text"
                        variant="outlined"
                        value={values?.batch?.customBatchNo}
                        onChange={handleChange}
                        error={Boolean(errors?.batch?.customBatchNo)}
                        helperText={errors?.batch?.customBatchNo}
                  />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                  <DatePickerComponent
                        config={{
                            name: "batch.expiryDate",
                            label: t("expiryDate"),
                            renderValue: ({ values }) => {
                                return values.batch.expiryDate
                            }
                        }}
                        formik={{
                            values, // values?.batch,
                            errors, // : errors?.batch,
                            touched, // : touched?.batch,
                            // handleChange,
                            setFieldValue
                        }}
                        sx={{
                            width: '100%'
                        }}
                        slotProps={{
                            textField: {
                                margin: 'dense',
                                size: 'small',
                                error: Boolean(errors?.batch?.expiryDate),
                                helperText: errors?.batch?.expiryDate,
                            },
                        }}
                  />
              </Grid>
          </>
    )

}

const RenderNew = ({ formik, t, getSlots, getRacks }) => {
    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;
    return (
          <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={6}>
                      <TextField
                            margin="dense"
                            size="small"
                            // autoFocus
                            fullWidth
                            label={t("itemName")}
                            type="text"
                            variant="outlined"
                            value={values?.itemName}
                            onChange={handleChange}
                            disabled
                      />
                  </Grid>

                  <Grid item xs={12} sm={12} md={6}>
                      <TextField
                            margin="dense"
                            size="small"
                            // autoFocus
                            fullWidth
                            label={t("code")}
                            type="text"
                            variant="outlined"
                            value={values?.code}
                            onChange={handleChange}
                            disabled
                      />
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                      <Divider/>
                  </Grid>

                  <Grid item xs={6} sm={6} md={6}>
                      <RenderRackSelect
                            formik={formik}
                            t={t}
                            getRacks={getRacks}
                      />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                      <RenderSlotSelect
                            t={t}
                            getSlots={getSlots}
                            formik={formik}
                      />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                      <TextField
                            margin="dense"
                            size="small"
                            // autoFocus
                            fullWidth
                            name={"verifyQty.value"}
                            label={t("qty")}
                            type="text"
                            variant="outlined"
                            value={values?.verifyQty?.value}
                            onChange={handleChange}
                            onFocus={event => {
                                event.target.select();
                            }}
                            error={Boolean(errors?.verifyQty?.value)}
                            helperText={errors?.verifyQty?.value}
                            InputProps={{
                                endAdornment: (
                                      <InputAdornment position="end">
                                          {` / ${parseFloat(values?.verifyQty?.maxQty)}`}
                                      </InputAdornment>
                                ),
                            }}
                      />
                  </Grid>

                  <Grid item xs={12} sm={12} md={6}><></>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                      <Divider>
                          <FormControlLabel
                                control={
                                    <Checkbox
                                          onChange={(event, newValue) => {
                                              // console.log('event', event.target.value, newValue)
                                              // setIsTrackBatch(newValue)
                                              setFieldValue(`batch`, {
                                                  ...values.batch,
                                                  enableTrackBatch: newValue
                                              })
                                          }}
                                          checked={Boolean(values?.batch?.enableTrackBatch)}
                                          // defaultChecked
                                    />
                                }
                                label={t("trackBatchReceives")}
                          />
                      </Divider>
                  </Grid>

                  <>
                      <RenderBatch
                            t={t}
                            formik={formik}
                      />
                  </>

                  <Grid item xs={12} sm={12} md={12}>
                      <Button
                            variant='contained'
                            fullWidth
                            type='submit'
                      >
                          <CommonIcons name={icons.save}/>
                          <Box sx={{ ml: 1 }}>
                              {t('saveChanges')}
                          </Box>
                      </Button>
                  </Grid>
              </Grid>
          </form>
    )
}

export default function VerifyInNew(
      {
          row,
          rackData,
          documentType,
          targetType,
          actionType,
          handleOnSuccess,
      }
) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const { promptMessage } = useSystemPrompt()
    const { setSystemLoading } = useAppContextFeatures()
    const { formatDateTimeDisplay } = useSystemUtils();

    const { performVerifyIn } = useDocumentValidation({ documentType, targetType, actionType })

    const [isTrackBatch, setIsTrackBatch] = useState(false)

    const {
        document_id: documentId,
        unverify_in_qty: unVerifyInQty
    } = row

    // console.log('row', row)

    const onSubmit = async (currentValues) => {

        setSystemLoading(true);

        try {

            // [
            // {
            //     "doc_item_id": "8979",
            //     "verify_in_qty": "2",
            //     "verify_in": {
            //         "slot_id": "184",
            //         "code": "fishballcarton",
            //         "custom_batch_no": "",
            //         "expiry_date": "2023-11-26"
            //     }
            // }
            // ]

            let item = {
                doc_item_id: currentValues?.docItemId,
                verify_in_qty: parseFloat(currentValues?.verifyQty?.value),
                verify_in: {
                    slot_id: currentValues?.slotId,
                    code: currentValues?.code
                }
            }

            if (currentValues?.batch?.enableTrackBatch === true) {
                item = {
                    ...item,
                    verify_in: {
                        ...item.verify_in,
                        custom_batch_no: currentValues?.batch?.customBatchNo,
                        expiry_date: currentValues?.batch?.expiryDate ? dayjs(currentValues.batch.expiryDate).format("YYYY-MM-DD"): ""
                    }
                }
            }

            const data = [item]

            // console.log('veridyData', item, currentValues)

            const response = await performVerifyIn({ id: documentId, data });

            promptMessage(true, t(`changesSaved`), response)

            // fetchData();
            if (handleOnSuccess) handleOnSuccess()

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

    }

    const getSchema = () => {

        return validationSchema
        // getValidationVerifySchema({ maxQty: unVerifyInQty, enableTrackBatch: isTrackBatch })
    }

    const formik = useFormik({
        initialValues,
        validationSchema: getSchema(),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;

    useEffect(() => {

        // assign the values into state formik
        setFieldValue(`code`, row?.code)
        setFieldValue(`docItemId`, row?.id)
        setFieldValue('itemName', row?.item_name)

        // maxQty
        setFieldValue(`verifyQty`, {
            ...values.verifyQty,
            value: "0",
            maxQty: parseFloat(unVerifyInQty)
        })

    }, [])

    const getRacks = () => {

        return rackData
    }

    const getSlots = ({ rackId }) => {

        if (!rackId) return []

        const rack = rackData.filter(item => item.id === rackId)

        return rack && rack.length ? rack[0]._slots: []
    }


    return (
          <Box
                sx={{
                    p: 2
                }}
          >
              <RenderNew
                    formik={formik}
                    t={t}
                    getSlots={getSlots}
                    getRacks={getRacks}
              />


          </Box>
    )
}